import { queryKeys } from '@qse/api'
import type { LoginData, User } from '@qse/constants'
import { loginValidationSchema } from '@qse/constants'
import { AppRoutesList } from '@qse/constants'
import { useUserStore } from '@qse/stores'
import { checkAppAccess, detectCurrentApp, storage } from '@qse/utils'
import { useQueryClient } from '@tanstack/react-query'
import { FormikHelpers } from 'formik'
import { useNavigate } from 'react-router-dom'

import { authService } from './auth-service'

export const useLogin = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { setUserAndAuth } = useUserStore()

  const handleLogin = async (
    values: LoginData,
    {
      setSubmitting,
      setStatus,
      onSuccess,
      onPasswordChangeRequired,
      onTOTPSetupRequired,
      onTOTPCodeRequired,
      onEmailVerificationRequired,
    }: Partial<FormikHelpers<LoginData>> & {
      onSuccess?: (response: User) => void
      onPasswordChangeRequired?: () => void
      onTOTPSetupRequired?: (totpSetup: any, isFirstLogin?: boolean) => void
      onTOTPCodeRequired?: () => void
      onEmailVerificationRequired?: (email: string) => void
    },
  ) => {
    try {
      // Detect current app type to determine domain
      const appDetection = detectCurrentApp()
      const domain = appDetection.appType === 'admin' ? 'admin' : 'dashboard'

      const result = await authService.login(values.email, values.password, domain)

      if (result.requiresEmailVerification) {
        console.log('🔄 Login - Email verification required')
        onEmailVerificationRequired?.(values.email)
        navigate(AppRoutesList.confirmEmail, {
          replace: true,
          state: { email: values.email },
        })

        return
      }

      if (result.requiresPasswordChange) {
        console.log('🔄 Login - Password change required')
        onPasswordChangeRequired?.()
        navigate(AppRoutesList.newPassword, { replace: true })

        return
      }

      if (result.requiresTOTPSetup) {
        console.log('🔄 Login - TOTP setup required')
        onTOTPSetupRequired?.(result.totpSetup, result.isFirstLogin)
        navigate(AppRoutesList.twoFA, {
          replace: true,
          state: {
            isFirstLogin: result.isFirstLogin,
            totpSetup: result.totpSetup,
          },
        })

        return
      }

      if (result.requiresTOTPCode) {
        console.log('🔄 Login - TOTP code required, navigating to 2FA verify')
        onTOTPCodeRequired?.()
        // Store TOTP setup data if available
        if (result.totpSetup) {
          storage.localStorage.setItem('totp_setup_data', JSON.stringify(result.totpSetup))
        }
        navigate(AppRoutesList.twoFAVerify, {
          replace: true,
          state: { totpSetup: result.totpSetup },
        })

        return
      }
      // Successful login
      if (result.user) {
        // Check if user has access to current app
        const userRole = result.user.userRole || result.user.role

        if (!checkAppAccess(userRole)) {
          console.log(`Login denied: User role ${userRole} not allowed for current app`)
          await authService.forceLogout()

          return
        }

        // Set user data in React Query cache to ensure consistency
        try {
          // Set the query data directly since we already have the user from auth-service
          queryClient.setQueryData(queryKeys.auth.me(), result.user)
          // Then invalidate to ensure fresh data on next fetch
          await queryClient.invalidateQueries({ queryKey: queryKeys.auth.me() })
        } catch (error) {
          console.error('Error setting user data in cache after login:', error)
          // Continue with login even if cache update fails
        }

        setUserAndAuth(result.user, true)
        // Call onSuccess callback if provided
        onSuccess?.(result.user)
      }
    } catch (error: any) {
      const errorMessage = authService.getUserFriendlyError(error, 'login')

      setStatus?.(errorMessage)
      // Note: Account switching is now handled automatically in authService.login()
      // No need for manual redirect on "already signed in" error
    } finally {
      setSubmitting?.(false)
    }
  }

  return {
    handleLogin,
    loginValidationSchema,
  }
}
